/**
 * /recover — soft-delete recovery page (Phase 5c-2).
 *
 * Lists everything the signed-in user can recover within the 30-day
 * window:
 *   - Orgs they were owner of (locked decision §10: owner-only recovery)
 *   - Workspaces in active orgs where they're owner+admin
 *
 * Each row shows "X days remaining" derived from `recoverableUntil`
 * (server-computed; we don't re-derive the constant here so they
 * can't drift). One Recover button per row, simple confirm modal.
 *
 * If the parent org of a workspace is itself soft-deleted, the
 * workspace doesn't appear here — recovering the org will surface
 * its workspaces again.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Building2, Layers } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { recoveryAPI, type RecoverableOrg, type RecoverableWorkspace } from '@/lib/api'
import { useIdentity } from '@/hooks/useIdentity'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatDate } from '@/lib/utils'
import { ConfirmDialog } from '@/components/ConfirmDialog'

type Recoverable =
  | { kind: 'org'; org: RecoverableOrg }
  | { kind: 'workspace'; workspace: RecoverableWorkspace }

export function Recover() {
  const { refreshIdentity } = useIdentity()
  const [orgs, setOrgs] = useState<RecoverableOrg[] | null>(null)
  const [workspaces, setWorkspaces] = useState<RecoverableWorkspace[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [pendingRecover, setPendingRecover] = useState<Recoverable | null>(null)
  const [busy, setBusy] = useState(false)

  async function load() {
    try {
      const data = await recoveryAPI.list()
      setOrgs(data.orgs)
      setWorkspaces(data.workspaces)
      setLoadError(null)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load')
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function handleConfirmRecover() {
    if (!pendingRecover) return
    setBusy(true)
    try {
      if (pendingRecover.kind === 'org') {
        await recoveryAPI.recoverOrg(pendingRecover.org._id)
      } else {
        await recoveryAPI.recoverWorkspace(pendingRecover.workspace._id)
      }
      await refreshIdentity()
      await load()
      setPendingRecover(null)
    } catch (err) {
      // Stay open with the error surfaced in the dialog
      throw err
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-dvh bg-muted/40">
      <div className="border-b bg-background">
        <div className="container mx-auto max-w-3xl px-4 h-14 flex items-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </Link>
          <div className="h-5 w-px bg-border" aria-hidden="true" />
          <div>
            <div className="text-xs text-muted-foreground">Recovery</div>
            <div className="text-sm font-medium leading-tight">Restore deleted items within the recovery window</div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-3xl p-4 pt-6 space-y-4">
        {loadError && (
          <Card>
            <CardContent className="p-6 space-y-2">
              <p className="text-sm text-destructive">{loadError}</p>
              <Button variant="outline" onClick={load}>Retry</Button>
            </CardContent>
          </Card>
        )}

        {orgs !== null && workspaces !== null && orgs.length === 0 && workspaces.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center text-sm text-muted-foreground">
              Nothing to recover. Items become unrecoverable after
              the recovery window expires.
            </CardContent>
          </Card>
        )}

        {orgs && orgs.length > 0 && (
          <Card>
            <CardContent className="p-6 space-y-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                Organizations
              </h2>
              <ul className="divide-y divide-border">
                {orgs.map(org => (
                  <RecoverRow
                    key={org._id}
                    title={org.name}
                    subtitle={`Slug: ${org.slug}`}
                    deletedAt={org.deletedAt}
                    recoverableUntil={org.recoverableUntil}
                    onRecover={() => setPendingRecover({ kind: 'org', org })}
                  />
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {workspaces && workspaces.length > 0 && (
          <Card>
            <CardContent className="p-6 space-y-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" />
                Workspaces
              </h2>
              <ul className="divide-y divide-border">
                {workspaces.map(ws => (
                  <RecoverRow
                    key={ws._id}
                    title={ws.name}
                    subtitle="Workspace"
                    deletedAt={ws.deletedAt}
                    recoverableUntil={ws.recoverableUntil}
                    onRecover={() => setPendingRecover({ kind: 'workspace', workspace: ws })}
                  />
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {pendingRecover && (
        <ConfirmDialog
          open
          onClose={() => !busy && setPendingRecover(null)}
          title="Recover this item?"
          description={
            pendingRecover.kind === 'org' ? (
              <>
                <strong>{pendingRecover.org.name}</strong> will be restored.
                All workspaces and data become accessible again.
              </>
            ) : (
              <>
                <strong>{pendingRecover.workspace.name}</strong> will be
                restored. Apps, variants, runs, and test data become
                accessible again.
              </>
            )
          }
          confirmLabel="Recover"
          confirmVariant="default"
          onConfirm={handleConfirmRecover}
          busy={busy}
        />
      )}

    </div>
  )
}

function RecoverRow({
  title,
  subtitle,
  deletedAt,
  recoverableUntil,
  onRecover,
}: {
  title: string
  subtitle: string
  deletedAt: string
  recoverableUntil: number
  onRecover: () => void
}) {
  const tz = useUserTimezone()
  const remainingMs = recoverableUntil - Date.now()
  const days = Math.max(0, Math.floor(remainingMs / (24 * 60 * 60 * 1000)))
  const hours = Math.max(0, Math.floor(remainingMs / (60 * 60 * 1000)) % 24)
  const remaining =
    days > 0 ? `${days} day${days === 1 ? '' : 's'} remaining` :
    hours > 0 ? `${hours} hour${hours === 1 ? '' : 's'} remaining` :
    'expires soon'

  return (
    <li className="py-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{title}</div>
        <div className="text-xs text-muted-foreground">
          {subtitle} · deleted {formatDate(deletedAt, tz)} · {remaining}
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onRecover}>Recover</Button>
    </li>
  )
}
